import * as React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Copyright from '../Layout/Copyright';
import StatCard, { StatCardProps } from './StatCard';
import MainChart from './MainChart';
import ProjectStatusChart from './ProjectStatusChart';
import RecentProjectsSidebar from './RecentProjectsSidebar';
import ProjectTable from './ProjectTable';


interface MainGridProps {
  projects: any[];
  teamMembers: any[];
  calculateProgress: (tasks: any[]) => number;
}

export default function MainGrid({ projects, teamMembers, calculateProgress }: MainGridProps) {
  const activeProjects = projects.filter(p => p.status === 'Active' || p.status === 'In Progress').length;
  const avgCompletion = projects.length > 0 
    ? Math.round(projects.reduce((acc, p) => acc + calculateProgress(p.tasks), 0) / projects.length) 
    : 0;

  const data: StatCardProps[] = [
    {
      title: 'Total Projects',
      value: projects.length.toString(),
      interval: 'Last 30 days',
      trend: 'neutral',
      data: [2, 3, 3, 4, 4, projects.length, projects.length],
    },
    {
      title: 'Active Projects',
      value: activeProjects.toString(),
      interval: 'Last 30 days',
      trend: projects.length > 0 ? 'up' : 'neutral',
      data: [1, 2, 2, 3, 3, activeProjects, activeProjects],
    },
    {
      title: 'Avg. Completion',
      value: `${avgCompletion}%`,
      interval: 'Last 30 days',
      trend: 'up',
      data: [45, 50, 52, 58, 62, avgCompletion, avgCompletion],
    },
    {
      title: 'Team Members',
      value: teamMembers.length.toString(),
      interval: 'Last 30 days',
      trend: 'neutral',
      data: [4, 4, 4, 4, 4, teamMembers.length, teamMembers.length],
    },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
        Overview
      </Typography>
      
      {/* Stats Cards Row */}
      <Grid container spacing={2} columns={12} sx={{ mb: 2 }}>
        {data.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={index}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* Main Charts and Highlights Row */}
      <Grid container spacing={2} columns={12} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 8, lg: 9 }}>
          <MainChart />
        </Grid>
        <Grid size={{ xs: 12, md: 4, lg: 3 }}>
          <Stack spacing={2}>
            <ProjectStatusChart projects={projects} />
            <RecentProjectsSidebar projects={projects} />
          </Stack>
        </Grid>
      </Grid>

      {/* Projects Table Row */}
      <Grid container spacing={2} columns={12}>
        <Grid size={{ xs: 12 }}>
          <ProjectTable projects={projects} />
        </Grid>
      </Grid>

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}
